import React, { FC, useEffect, useRef, useState, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  LineController,
  BarController,
  ChartData,
  ChartOptions,
  ArcElement,
  ScatterController,
  BubbleController,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { format, parseISO } from 'date-fns';
import type { FinancialHealthStats } from '../../types/financialStats';
import { ChartOverlayWrapper } from './ChartOverlayWrapper';
import ChartExpandModal from './ChartExpandModal';
import { useMobileChart } from '../../context/MobileChartContext';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  LineController,
  BarController,
  ArcElement,
  ScatterController,
  BubbleController
);

interface VeraScoreChartProps {
  data: FinancialHealthStats[];
  showDebug?: boolean;
  monthsToShow?: number;
  onExpandClick?: React.MutableRefObject<any>;
  /** BUG-UI-071A: Run ID for evidence modal */
  runId?: string;
  /** BUG-UI-071A: Member ID for historical evidence */
  memberId?: string;
  /** BUG-UI-071A: Member name for modal display */
  memberName?: string;
}

// Line styles for different visualizations
const LINE_STYLES = {
  STRAIGHT: 0,     // Direct lines between points
  CURVED: 0.4,     // Smooth curved lines (good for trends)
};

// Main color for VeraScore charts
export const CHART_COLOR = '#1C4583'; // Primary blue

const VeraScoreChart: FC<VeraScoreChartProps> = ({
  data,
  showDebug,
  monthsToShow = 24, // Default to 24 months now
  onExpandClick,
}) => {
  const chartRef = useRef<any>(null);
  const [visibleData, setVisibleData] = useState<FinancialHealthStats[]>(
    data.slice(-monthsToShow)
  );
  const [viewWindow, setViewWindow] = useState({
    start: Math.max(0, data.length - monthsToShow),
    end: data.length
  });
  const containerRef = useRef<HTMLDivElement>(null);

  // Get mobile context
  const { isMobile } = useMobileChart();

  // State for chart expand modal (fullscreen chart view)
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Add touch state tracking around line 70
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Expose the openModal function through the onExpandClick prop
  // FIX: Opens ChartExpandModal (fullscreen chart) instead of evidence modal
  useEffect(() => {
    if (onExpandClick) {
      const handleExpandClick = () => {
        if (import.meta.env.DEV) console.log('Opening ChartExpandModal from VeraScoreChart');
        setIsModalOpen(true);
      };

      onExpandClick.current = onExpandClick.current || {};
      onExpandClick.current.openModal = handleExpandClick;

      if (import.meta.env.DEV) console.log('Set openModal on ref.current in VeraScoreChart:', onExpandClick.current);
    }
  }, [isMobile, onExpandClick]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const ctx = chart.ctx;

    // Create improved gradient for the line with glassmorphic style
    const lineGradient = ctx.createLinearGradient(0, 0, 0, 300);
    lineGradient.addColorStop(0, 'rgba(66, 133, 244, 0.95)'); // Brighter blue at top
    lineGradient.addColorStop(0.6, 'rgba(63, 103, 186, 0.9)'); // Brand blue in middle
    lineGradient.addColorStop(1, 'rgba(55, 48, 163, 0.8)');    // Deep indigo at bottom

    // Create improved gradient for the area with glassmorphic style
    const areaGradient = ctx.createLinearGradient(0, 0, 0, 300);
    areaGradient.addColorStop(0, 'rgba(66, 133, 244, 0.3)');  // Brighter blue at top (higher opacity)
    areaGradient.addColorStop(0.5, 'rgba(63, 103, 186, 0.15)'); // Brand blue in middle (medium opacity)
    areaGradient.addColorStop(1, 'rgba(255, 255, 255, 0.05)');  // Almost transparent at bottom

    if (chart.data.datasets) {
      chart.data.datasets[0].backgroundColor = areaGradient;
      chart.data.datasets[0].borderColor = lineGradient;
    }

    chart.update();
  }, [visibleData]);

  // Find key data points (peaks, valleys, significant changes)
  const findKeyDataPoints = () => {
    if (visibleData.length < 3) return [];

    let keyPoints: number[] = [];
    
    // Always include first and last points
    keyPoints.push(0);
    keyPoints.push(visibleData.length - 1);
    
    // Find peaks and valleys
    for (let i = 1; i < visibleData.length - 1; i++) {
      const prev = visibleData[i-1].verascore ?? 0;
      const current = visibleData[i].verascore ?? 0;
      const next = visibleData[i+1].verascore ?? 0;
      
      // Peak
      if (current > prev && current > next) {
        keyPoints.push(i);
      }
      // Valley
      else if (current < prev && current < next) {
        keyPoints.push(i);
      }
      // Significant change (more than 10 points)
      else if (Math.abs(current - prev) > 10 || Math.abs(current - next) > 10) {
        keyPoints.push(i);
      }
    }
    
    // Keep only unique indices
    return [...new Set(keyPoints)].sort((a, b) => a - b);
  };

  // Calculate simple moving average trend line
  const calculateTrendLine = (data: number[]): number[] => {
    if (data.length <= 2) return data;
    
    // Use linear regression to calculate trend line
    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumXX = 0;
    const n = data.length;
    
    // Calculate sums for linear regression formula
    for (let i = 0; i < n; i++) {
      sumX += i;
      sumY += data[i];
      sumXY += i * data[i];
      sumXX += i * i;
    }
    
    // Calculate slope and y-intercept
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    
    // Generate trend line points
    return data.map((_, i) => intercept + slope * i);
  };

  const keyPoints = findKeyDataPoints();

  const chartData: ChartData<'line', number[]> = {
    labels: visibleData.map(d => d.month),
    datasets: [
      {
        label: 'VeraScore',
        data: visibleData.map(d => d.verascore ?? 0),
        fill: true,
        tension: LINE_STYLES.CURVED,
        borderWidth: 2.5,
        pointRadius: (ctx) => {
          // Show points only for key points to reduce visual clutter
          return keyPoints.includes(ctx.dataIndex) ? 3 : 0;
        },
        pointHoverRadius: 5,
        pointBackgroundColor: 'rgba(255, 255, 255, 0.9)',
        pointBorderColor: 'rgba(63, 103, 186, 1)',
        pointBorderWidth: 2,
        spanGaps: true,
        borderCapStyle: 'round' as const,
        borderJoinStyle: 'round' as const,
      },
      {
        label: 'Trend',
        data: calculateTrendLine(visibleData.map(d => d.verascore ?? 0)),
        fill: false,
        tension: 0.2,
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 0,
        borderColor: 'rgba(107, 114, 128, 0.8)',
        spanGaps: false,
      }
    ]
  };

  // Configure x-axis labels (months)
  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        bottom: 20,
        left: 0,
        right: 0,
        top: 2
      }
    },
    animation: {
      duration: 750,
      easing: 'easeOutQuart' as const,
    },
    elements: {
      line: {
        tension: LINE_STYLES.CURVED,
      },
      point: {
        radius: (ctx) => {
          if (!ctx.dataIndex) return 0;
          return keyPoints.includes(ctx.dataIndex) ? 3 : 0;
        },
        hitRadius: 10,
        hoverRadius: 5,
        hoverBorderWidth: 2
      }
    },
    transitions: {
      active: {
        animation: {
          duration: 150 // Slightly faster transitions
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'nearest' as const,
      axis: 'x' as const,
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          autoSkip: true,
          maxTicksLimit: 12,
          font: {
            size: 11,
            family: "'Manrope', sans-serif",
            weight: 'bold'
          },
          padding: 1,
          callback: function(_, index) {
            try {
              const date = parseISO(visibleData[index].month);
              return format(date, 'MMM yyyy');
            } catch (e) {
              return '';
            }
          }
        },
        border: {
          display: false,
        },
        offset: false,
      },
      y: {
        min: 0,
        max: 100,
        grid: {
          color: 'rgba(226, 232, 240, 0.5)',
        },
        ticks: {
          stepSize: 20,
          font: {
            size: 12,
            family: "'Manrope', sans-serif",
            weight: 'bold'
          },
          color: 'rgba(100, 116, 139, 0.9)',
          padding: 2,
          callback: function(value: any) {
            return value;
          }
        },
        border: {
          dash: [4, 4],
          display: false,
        },
        position: 'left' as const,
        offset: false,
      },
    },
    plugins: {
      tooltip: {
        enabled: true,
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#334155',
        bodyColor: '#1C4583',
        borderColor: 'rgba(226, 232, 240, 0.8)',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        titleMarginBottom: 8,
        footerMarginTop: 8,
        titleFont: {
          weight: '600' as any,
          size: 12,
          family: "'Manrope', sans-serif",
        },
        bodyFont: {
          weight: '500' as any,
          size: 12,
          family: "'Manrope', sans-serif",
        },
        callbacks: {
          title: (tooltipItems: any) => {
            try {
              if (!tooltipItems?.[0]?.label) return '';
              const dataIndex = tooltipItems[0].dataIndex;
              const date = parseISO(visibleData[dataIndex].month);
              return format(date, 'MMMM yyyy');
            } catch (e) {
              return tooltipItems?.[0]?.label || '';
            }
          },
          label: (context: any) => {
            if (context.dataset.label === 'Trend') {
              return `Average Score: ${context.raw !== null ? Math.round(context.raw) : '--'}`;
            }
            
            const score = context.raw;
            const monthData = visibleData[context.dataIndex];
            
            // Get previous month data for comparison if available
            let changeText = '';
            if (context.dataIndex > 0) {
              const prevScore = visibleData[context.dataIndex - 1].verascore ?? 0;
              const change = score - prevScore;
              changeText = `Change: ${change >= 0 ? '+' : ''}${Math.round(change)}`;
            }
            
            return [
              `Month Score: ${score !== null ? Math.round(score) : '--'}`,
              `Status: ${monthData.verascore_health || 'Unknown'}`,
              changeText
            ].filter(Boolean); // Filter out empty change text if not available
          },
        },
      },
      legend: {
        display: false,
      },
    },
  };

  // Fix the handleDataChange function to handle circular dependencies
  const handleDataChange = useCallback((newVisibleData: FinancialHealthStats[]) => {
    // Temporarily disable animations when changing data
    const chart = chartRef.current;
    if (chart) {
      // Store original animation settings
      const originalAnimation = chart.options.animation;
      
      // Disable animations and update data in one go
      chart.options.animation = false;
      
      // Update the data directly in the chart to avoid re-renders
      chart.data.labels = newVisibleData.map(d => d.month);
      chart.data.datasets[0].data = newVisibleData.map(d => d.verascore ?? 0);
      if (chart.data.datasets[1]) {
        chart.data.datasets[1].data = calculateTrendLine(newVisibleData.map(d => d.verascore ?? 0));
      }
      
      // Update the chart with 'none' mode for instant update
      chart.update('none');
      
      // Update state after chart is updated
      setVisibleData(newVisibleData);
      
      // Re-enable animations after a short delay
      setTimeout(() => {
        if (chart) {
          chart.options.animation = originalAnimation;
        }
      }, 100);
    } else {
      setVisibleData(newVisibleData);
    }
  }, []);

  // If on mobile device, register this chart component with the ref
  useEffect(() => {
    if (onExpandClick && onExpandClick.current) {
      // Store the chart content for mobile overlay
      onExpandClick.current.getChartComponent = () => (
        <Chart
          type="line"
          data={chartData}
          options={options}
        />
      );
      
      // For debugging
      if (import.meta.env.DEV) console.log('Registered chart component in VeraScoreChart with ref', onExpandClick.current);
    }
  }, [isMobile, chartData, options, onExpandClick]);

  // Add touch navigation handler functions after chartRef declaration
  // Handle touch/mouse start
  const handleDragStart = useCallback((clientX: number) => {
    setTouchStartX(clientX);
    setIsDragging(true);
  }, []);

  // Handle touch/mouse move
  const handleDragMove = useCallback((clientX: number) => {
    if (!isDragging || touchStartX === null) return;
    
    const deltaX = touchStartX - clientX;
    if (Math.abs(deltaX) < 10) return; // Ignore small movements
    
    const dragDirection = deltaX > 0 ? 'right' : 'left';
    const dragAmount = Math.ceil(Math.abs(deltaX) / 20); // Scale factor for drag sensitivity
    
    let newStart = viewWindow.start;
    let newEnd = viewWindow.end;
    
    if (dragDirection === 'right') {
      newStart = Math.max(0, Math.min(data.length - monthsToShow, newStart + dragAmount));
      newEnd = Math.min(data.length, newStart + monthsToShow);
    } else if (dragDirection === 'left') {
      newStart = Math.max(0, Math.min(data.length - monthsToShow, newStart - dragAmount));
      newEnd = Math.min(data.length, newStart + monthsToShow);
    }
    
    if (newStart !== viewWindow.start || newEnd !== viewWindow.end) {
      setViewWindow({ start: newStart, end: newEnd });
      // Update visible data based on window
      const newVisibleData = data.slice(newStart, newEnd);
      handleDataChange(newVisibleData);
    }
  }, [touchStartX, isDragging, data, monthsToShow, viewWindow, handleDataChange]);

  // Handle touch/mouse end
  const handleDragEnd = useCallback(() => {
    setTouchStartX(null);
    setIsDragging(false);
  }, []);

  // Add event listeners via useEffect
  useEffect(() => {
    const chartContainer = containerRef.current;
    if (!chartContainer) return;
    
    const handleTouchStart = (e: TouchEvent) => {
      handleDragStart(e.touches[0].clientX);
    };
    
    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;
      handleDragMove(e.touches[0].clientX);
    };
    
    const handleTouchEnd = () => {
      handleDragEnd();
    };
    
    const handleMouseDown = (e: MouseEvent) => {
      handleDragStart(e.clientX);
    };
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      handleDragMove(e.clientX);
    };
    
    const handleMouseUp = () => {
      handleDragEnd();
    };
    
    // Add event listeners
    chartContainer.addEventListener('touchstart', handleTouchStart);
    chartContainer.addEventListener('touchmove', handleTouchMove);
    chartContainer.addEventListener('touchend', handleTouchEnd);
    chartContainer.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    // Cleanup
    return () => {
      chartContainer.removeEventListener('touchstart', handleTouchStart);
      chartContainer.removeEventListener('touchmove', handleTouchMove);
      chartContainer.removeEventListener('touchend', handleTouchEnd);
      chartContainer.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleDragStart, handleDragMove, handleDragEnd]);

  // Add keyframe animation at top of file after imports
  // Add to head style section after the ChartJS.register()
  useEffect(() => {
    // Add keyframe animation for swipe indicator
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      @keyframes fadeInOut {
        0% { opacity: 0; }
        25% { opacity: 1; }
        75% { opacity: 1; }
        100% { opacity: 0; }
      }
    `;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Add a useEffect to initialize with the full 24 months on component mount
  useEffect(() => {
    // Initialize with full dataset of up to 24 months on mount
    if (data.length > 0) {
      const dataToShow = data.slice(Math.max(0, data.length - monthsToShow));
      setVisibleData(dataToShow);
      
      // Log to debug the data being loaded
      if (import.meta.env.DEV) console.log(`Initializing VeraScoreChart with ${dataToShow.length} of ${data.length} total months`);
      if (import.meta.env.DEV) console.log('First month:', dataToShow[0]?.month, 'Last month:', dataToShow[dataToShow.length - 1]?.month);
    }
  }, [data, monthsToShow]);

  // Add logging to track data length in the component
  if (import.meta.env.DEV) console.log('VeraScoreChart data length:', data.length, 'monthsToShow:', monthsToShow);
  if (import.meta.env.DEV) console.log('viewWindow:', viewWindow.start, viewWindow.end);
  if (import.meta.env.DEV) console.log('visibleData length:', visibleData.length);

  // Fix the visibleData state to update properly based on viewWindow
  useEffect(() => {
    // Update visibleData whenever viewWindow changes
    if (data.length > 0) {
      const newVisibleData = data.slice(viewWindow.start, viewWindow.end);
      setVisibleData(newVisibleData);
    }
  }, [viewWindow, data]);

  return (
    <div className="relative w-full flex flex-col" style={{ 
      height: '100%',
      minHeight: '100%',
      marginBottom: '0',
      paddingBottom: '0',
      position: 'relative',
      borderRadius: '8px',
      overflow: 'hidden'
    }}>
      {/* Add debug info for development environment */}
      {process.env.NODE_ENV === 'development' && showDebug && (
        <div className="absolute top-0 right-0 bg-white/80 p-2 text-xs text-slate-500 z-10 rounded border">
          <div>Total months: {data.length}</div>
          <div>Visible: {visibleData.length}</div>
          <div>Window: {viewWindow.start} - {viewWindow.end}</div>
        </div>
      )}
      
      {/* Navigation header with months and navigation buttons */}
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm text-slate-700">
          {viewWindow.end === data.length ? (
            <>Most recent <span className="font-semibold">{viewWindow.end - viewWindow.start}</span> of <span className="font-semibold">{data.length}</span> months</>
          ) : (
            <><span className="font-semibold">{viewWindow.end - viewWindow.start}</span> of <span className="font-semibold">{data.length}</span> months shown</>
          )}
          {data.length < 24 && <span className="ml-1 text-amber-500 text-xs">(Limited data available)</span>}
        </div>

        <div className="flex items-center gap-2">
          <button 
            className="text-xs px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
            onClick={() => {
              const newStart = Math.max(0, viewWindow.start - Math.floor(monthsToShow / 2));
              const newEnd = Math.min(data.length, newStart + monthsToShow);
              
              // Update view window
              setViewWindow({ start: newStart, end: newEnd });
              
              // Also update visibleData through handleDataChange
              handleDataChange(data.slice(newStart, newEnd));
            }}
          >
            ← Earlier
          </button>
          
          <button 
            className="text-xs px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
            onClick={() => {
              const newStart = Math.min(data.length - monthsToShow, viewWindow.start + Math.floor(monthsToShow / 2));
              const newEnd = Math.min(data.length, newStart + monthsToShow);
              
              // Update view window
              setViewWindow({ start: newStart, end: newEnd });
              
              // Also update visibleData through handleDataChange
              handleDataChange(data.slice(newStart, newEnd));
            }}
          >
            Later →
          </button>
        </div>
      </div>
      
      {/* Glassmorphic chart container */}
      <div 
        ref={containerRef}
        className="p-0 rounded-xl overflow-hidden w-full flex-grow"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(8px)',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.8)',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.03), 0 1px 6px rgba(0, 0, 0, 0.02)',
          height: 'calc(100% - 40px)', // Subtract space for x-axis labels
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          cursor: 'grab'
        }}
      >
        <div 
          className="relative h-full pb-6"
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        > 
          <ChartOverlayWrapper overlay={null} showStatsButton={false}>
            <Chart
              ref={chartRef}
              type="line"
              data={chartData}
              options={options}
            />
          </ChartOverlayWrapper>
        </div>
      </div>

      {/* FIX: Use ChartExpandModal for fullscreen chart expansion (like other factor charts) */}
      <ChartExpandModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        data={data}
        title="VeraScore"
        dataKey="verascore"
        color={CHART_COLOR}
        yAxisFormatter={(value: number) => value.toString()}
        min={0}
        max={100}
      />
    </div>
  );
};

export { VeraScoreChart };
